/**
 * vpv-cbb-product-form.js
 * --------------------------------------------------------------------------
 * Wires up each [data-cbb-section] independently:
 *
 *   1. On `cbb:variant:changed` from <cbb-variants>:
 *        - Update price display (one-time)
 *        - Update ATC button label / disabled state
 *        - Update ATC button price span (immediate, variant-only)
 *        - Re-fetch selling-plan-aware price via Section Rendering API
 *          (so Skio's discounted price stays accurate when the variant flips)
 *
 *   2. On form submit:
 *        - Prevent default
 *        - POST to /cart/add.js with the form's FormData
 *          (this naturally includes Skio's <input name="selling_plan">)
 *        - On success: dispatch `cbb:cart:added` and try common cart drawer openers
 *        - On failure: surface the error inline
 *
 *   3. On selling plan change (Skio updates [name="selling_plan"]):
 *        - Re-fetch and update both the price display and the button price
 *
 * Button structure:
 *   <button data-cbb-atc>
 *     <span data-cbb-atc-text>
 *       <span data-cbb-atc-price>$100.00</span>
 *       <span class="cbb-buy__atc-separator"> - </span>
 *       <span data-cbb-atc-label>Add to cart</span>
 *     </span>
 *     <span data-cbb-atc-spinner>...</span>
 *   </button>
 *
 * Labels (default / sold-out / unavailable) are read from data attributes on
 * the button so JS can swap state correctly regardless of which label was
 * rendered server-side.
 * --------------------------------------------------------------------------
 */
(function () {
  const SECTION_SELECTOR = '[data-cbb-section]';

  class CbbController {
    constructor(section) {
      this.section = section;
      this.sectionId = section.dataset.sectionId;
      this.productHandle = section.dataset.productHandle;
      this.productUrl = section.dataset.productUrl;
      this.form = section.querySelector('[data-cbb-form]');
      if (!this.form) return;

      this.atc = this.form.querySelector('[data-cbb-atc]');
      this.atcPrice = this.form.querySelector('[data-cbb-atc-price]');
      this.atcLabel = this.form.querySelector('[data-cbb-atc-label]');
      this.atcSeparator = this.form.querySelector('.cbb-buy__atc-separator');
      this.atcSpinner = this.form.querySelector('[data-cbb-atc-spinner]');
      this.errorEl = this.form.querySelector('[data-cbb-atc-error]');
      this.priceEl = this.section.querySelector('[data-cbb-price]');
      this.variantInput = this.form.querySelector('[data-cbb-variant-input]');

      // Read button labels from data attributes (preserves merchant overrides).
      if (this.atc) {
        this.defaultButtonLabel = this.atc.dataset.cbbDefaultLabel || 'Add to cart';
        this.soldOutText = this.atc.dataset.cbbSoldOutLabel || 'Sold out';
        this.unavailableText = this.atc.dataset.cbbUnavailableLabel || 'Unavailable';
      } else {
        this.defaultButtonLabel = 'Add to cart';
        this.soldOutText = 'Sold out';
        this.unavailableText = 'Unavailable';
      }

      this._onVariantChange = this._onVariantChange.bind(this);
      this._onSubmit = this._onSubmit.bind(this);
      this._onSellingPlanChange = this._onSellingPlanChange.bind(this);

      this.section.addEventListener('cbb:variant:changed', this._onVariantChange);
      this.form.addEventListener('submit', this._onSubmit);

      // Watch for Skio (or any other app) injecting / changing [name="selling_plan"].
      this._watchSellingPlan();
    }

    _watchSellingPlan() {
      // Listen for change events on any current/future selling_plan input inside the form.
      this.form.addEventListener('change', (e) => {
        if (e.target && e.target.name === 'selling_plan') {
          this._onSellingPlanChange();
        }
      });

      // Skio sometimes sets the value programmatically without firing a change event,
      // and may re-render its widget in place. Observe the form and debounce so we
      // don't spam /products/handle?section_id= during widget initialization.
      const debouncedRefresh = debounce(() => this._refreshPrice(), 150);
      const observer = new MutationObserver((mutations) => {
        // Only refresh if a selling_plan input was added/removed/changed.
        const relevant = mutations.some((m) => {
          if (m.type === 'attributes' && m.target && m.target.name === 'selling_plan') return true;
          if (m.type === 'childList') {
            for (const node of [...m.addedNodes, ...m.removedNodes]) {
              if (node.nodeType === 1 && (node.name === 'selling_plan' || node.querySelector?.('[name="selling_plan"]'))) {
                return true;
              }
            }
          }
          return false;
        });
        if (relevant) debouncedRefresh();
      });
      observer.observe(this.form, { childList: true, subtree: true, attributes: true, attributeFilter: ['value'] });
      this._mutationObserver = observer;
    }

    _onVariantChange(event) {
      const variant = event.detail && event.detail.variant;
      this._updateButtonState(variant);
      this._updatePriceFromVariant(variant);
      // Then re-fetch the section to get selling-plan-aware pricing.
      this._refreshPrice();
    }

    _onSellingPlanChange() {
      this._refreshPrice();
    }

    _updateButtonState(variant) {
      if (!this.atc) return;

      if (!variant) {
        this.atc.disabled = true;
        if (this.atcLabel) this.atcLabel.textContent = this.unavailableText;
        this._toggleButtonPrice(false);
        return;
      }
      if (!variant.available) {
        this.atc.disabled = true;
        if (this.atcLabel) this.atcLabel.textContent = this.soldOutText;
        this._toggleButtonPrice(false);
        return;
      }
      this.atc.disabled = false;
      if (this.atcLabel) this.atcLabel.textContent = this.defaultButtonLabel;
      this._toggleButtonPrice(true);
    }

    _toggleButtonPrice(show) {
      if (this.atcPrice) this.atcPrice.classList.toggle('is-hidden', !show);
      if (this.atcSeparator) this.atcSeparator.classList.toggle('is-hidden', !show);
    }

    _updatePriceFromVariant(variant) {
      if (!variant) return;

      // Update cbb-price block
      if (this.priceEl) {
        const sale = this.priceEl.querySelector('[data-cbb-price-sale]');
        const regular = this.priceEl.querySelector('[data-cbb-price-regular]');
        const compare = this.priceEl.querySelector('[data-cbb-price-compare]');
        const unavailable = this.priceEl.querySelector('[data-cbb-price-unavailable]');

        if (unavailable) unavailable.classList.add('is-hidden');

        const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;

        if (onSale) {
          if (sale) {
            sale.textContent = formatMoney(variant.price);
            sale.classList.remove('is-hidden');
          }
          if (compare) {
            compare.textContent = formatMoney(variant.compare_at_price);
            compare.classList.remove('is-hidden');
          }
          if (regular) regular.classList.add('is-hidden');
        } else {
          if (regular) {
            regular.textContent = formatMoney(variant.price);
            regular.classList.remove('is-hidden');
          }
          if (sale) sale.classList.add('is-hidden');
          if (compare) compare.classList.add('is-hidden');
        }
      }

      // Update ATC button price immediately (variant-only — selling plan refresh follows).
      if (this.atcPrice && variant.available) {
        this.atcPrice.textContent = formatMoney(variant.price);
      }
    }

    /**
     * Fetch the rendered section with current variant + selling plan in the URL,
     * so Liquid recomputes selling_plan_allocations and we get the accurate price.
     * Updates both the [data-cbb-price] block and the [data-cbb-atc-price] span
     * with the Liquid-formatted (Shopify.formatMoney-fidelity) price string.
     */
    async _refreshPrice() {
      if (!this.priceEl && !this.atcPrice) return;
      const variantId = this.variantInput && this.variantInput.value;
      if (!variantId) return;

      const sellingPlanInput = this.form.querySelector('[name="selling_plan"]');
      const sellingPlan = sellingPlanInput && sellingPlanInput.value;

      const params = new URLSearchParams();
      params.set('variant', variantId);
      if (sellingPlan) params.set('selling_plan', sellingPlan);
      params.set('section_id', this.sectionId);

      try {
        const res = await fetch(`${this.productUrl}?${params.toString()}`);
        if (!res.ok) return;
        const html = await res.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');

        // Update cbb-price block
        if (this.priceEl) {
          const newPrice = parsed.querySelector(`[data-section-id="${this.sectionId}"] [data-cbb-price]`);
          if (newPrice) {
            this.priceEl.innerHTML = newPrice.innerHTML;
            for (const attr of newPrice.attributes) {
              if (attr.name.startsWith('data-current-')) {
                this.priceEl.setAttribute(attr.name, attr.value);
              }
            }
          }
        }

        // Update ATC button price with the Liquid-rendered (selling-plan-aware) price.
        if (this.atcPrice) {
          const newAtcPrice = parsed.querySelector(`[data-section-id="${this.sectionId}"] [data-cbb-atc-price]`);
          if (newAtcPrice) {
            this.atcPrice.textContent = newAtcPrice.textContent.trim();
          }
        }
      } catch (err) {
        // Non-fatal — price will just stay on the variant-derived value.
        console.warn('[cbb] price refresh failed', err);
      }
    }

    async _onSubmit(event) {
      event.preventDefault();
      this._setLoading(true);
      this._clearError();

      const formData = new FormData(this.form);
      // Strip Liquid form_type so /cart/add.js doesn't choke on it; harmless but tidy.
      formData.delete('form_type');
      formData.delete('utf8');

      // Ask Shopify to render the cart sections back to us so the drawer + count
      // bubble update without a page refresh (Section Rendering API).
      const cartSections = this._cartSections();
      if (cartSections.length) {
        formData.append('sections', cartSections.map((s) => s.section).join(','));
        formData.append('sections_url', window.location.pathname);
      }

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData
        });

        const data = await res.json();

        if (!res.ok) {
          this._showError(data.description || data.message || 'Could not add to cart.');
          return;
        }

        // Inject the freshly rendered cart sections into the live DOM.
        if (data.sections) this._renderCartSections(data.sections, cartSections);

        // Notify the rest of the theme.
        document.dispatchEvent(new CustomEvent('cbb:cart:added', {
          detail: { item: data, sectionId: this.sectionId }
        }));

        this._openCartDrawer();
      } catch (err) {
        this._showError('Network error. Please try again.');
      } finally {
        this._setLoading(false);
      }
    }

    /**
     * The cart sections to request + how to apply them. `section` is the Shopify
     * section name (filename), `target` is the live element to update, `source`
     * is the selector to pull from the rendered HTML. Each entry is only included
     * if its target exists in the DOM.
     *
     * Canopy theme: sections/cart-drawer.liquid + sections/cart-icon-bubble.liquid.
     */
    _cartSections() {
      const bubbleTarget =
        document.querySelector('#shopify-section-cart-icon-bubble') ||
        document.querySelector('#cart-icon-bubble') ||
        document.querySelector('.cart-count-bubble, [data-cart-icon-bubble]');

      const map = [
        {
          section: 'cart-drawer',
          selector: '.cart-drawer__content',
          target: document.querySelector('#cart-drawer__content')
        },
        {
          section: 'cart-icon-bubble',
          selector: '.shopify-section, #cart-icon-bubble, .cart-count-bubble',
          target: bubbleTarget
        }
      ];
      return map.filter((m) => m.target);
    }

    /**
     * Replace each live cart target with the matching freshly rendered markup.
     */
    _renderCartSections(rendered, cartSections) {
      cartSections.forEach((m) => {
        const html = rendered[m.section];
        if (!html || !m.target) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const source =
          parsed.querySelector(m.selector.split(',')[0].trim()) ||
          parsed.querySelector('.shopify-section') ||
          parsed.body;
        if (source) m.target.innerHTML = source.innerHTML;
      });
    }

    _setLoading(isLoading) {
      if (!this.atc) return;
      this.atc.classList.toggle('is-loading', isLoading);
      this.atc.disabled = isLoading;
      if (this.atcSpinner) this.atcSpinner.style.display = isLoading ? 'inline-flex' : 'none';
    }

    _showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.classList.remove('visually-hidden');
    }

    _clearError() {
      if (!this.errorEl) return;
      this.errorEl.textContent = '';
      this.errorEl.classList.add('visually-hidden');
    }

    /**
     * Opens the cart drawer in a theme-agnostic way.
     * Tries the most common patterns in order, then falls back to a
     * `cbb:cart:open` event the merchant can wire to anything.
     */
    _openCartDrawer() {
      const tries = [
        // Dawn / Horizon-style cart-drawer custom element with .open()
        () => {
          const drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.open === 'function') { drawer.open(); return true; }
          return false;
        },
        // Some themes expose a global
        () => {
          if (window.theme && typeof window.theme.openCartDrawer === 'function') {
            window.theme.openCartDrawer(); return true;
          }
          return false;
        },
        // Minimog/Impulse/various — dispatch on document
        () => {
          document.dispatchEvent(new CustomEvent('cart:open'));
          return true; // best-effort, no way to confirm
        }
      ];

      for (const fn of tries) {
        try { if (fn()) break; } catch (_) {}
      }

      // Always emit our own event for explicit wiring.
      document.dispatchEvent(new CustomEvent('cbb:cart:open'));
    }
  }

  // ---------------------------------------------------------------------------
  // Money formatter. Prefers the theme's own Shopify.formatMoney (which uses
  // Shopify's money_format setting, e.g. "${{ amount }}") so the JS-formatted
  // price matches the Liquid-rendered price exactly — no "US$ → $" flash
  // between the immediate update and the section-render fetch result.
  //
  // Falls back to Intl.NumberFormat with currencyDisplay: 'narrowSymbol' which
  // produces "$" instead of "US$" for USD.
  // ---------------------------------------------------------------------------
  function formatMoney(cents) {
    if (typeof window.Shopify !== 'undefined' && typeof window.Shopify.formatMoney === 'function') {
      try {
        return window.Shopify.formatMoney(cents);
      } catch (_) { /* fall through */ }
    }

    const amount = (cents / 100).toFixed(2);
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol'
      }).format(amount);
    } catch (_) {
      return `$${amount}`;
    }
  }

  function debounce(fn, ms) {
    let t;
    return function (...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), ms);
    };
  }

  // ---------------------------------------------------------------------------
  // Bootstrap
  // ---------------------------------------------------------------------------
  function init() {
    document.querySelectorAll(SECTION_SELECTOR).forEach((section) => {
      if (section.__cbbInitialized) return;
      section.__cbbInitialized = true;
      new CbbController(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init on Shopify section reload (theme editor)
  document.addEventListener('shopify:section:load', (e) => {
    if (e.target.querySelector(SECTION_SELECTOR)) init();
  });
})();
